/**
 * VAD (Voice Activity Detection) Hook
 * v6.0 新增：基于 Silero VAD 的实时语音活动检测
 * 
 * 使用 @ricky0123/vad-web 库，在浏览器中运行 Silero VAD ONNX 模型
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { MicVAD } from '@ricky0123/vad-web'

export interface VADOptions {
  /** 语音开始回调 */
  onSpeechStart?: () => void
  /** 语音结束回调，返回音频数据 */
  onSpeechEnd?: (audio: Float32Array, duration: number) => void
  /** VAD 状态变化回调 */
  onVADMisfire?: () => void
  /** 错误回调 */
  onError?: (error: string) => void
  /** 采样率，默认 16000 */
  sampleRate?: number
  /** 语音概率阈值，默认 0.5 */
  positiveSpeechThreshold?: number
  /** 静音概率阈值，默认 0.35 */
  negativeSpeechThreshold?: number
  /** 语音前保留时间(ms)，默认 100 */
  preSpeechPadMs?: number
  /** 静音后延迟时间(ms)，用于判断语音结束，默认 300 */
  redemptionMs?: number
  /** 最小语音时长(ms)，默认 150 */
  minSpeechMs?: number
}

export interface VADState {
  /** VAD 是否已初始化 */
  isInitialized: boolean
  /** VAD 是否正在监听 */
  isListening: boolean
  /** 当前是否检测到语音 */
  isSpeaking: boolean
  /** 是否暂停（用于 AEC） */
  isPaused: boolean
  /** 浏览器是否支持 */
  isSupported: boolean
  /** 模型是否正在加载 */
  isLoading: boolean
  /** 错误信息 */
  error: string | null
}

/**
 * VAD Hook
 * 
 * @example
 * ```tsx
 * const { isListening, isSpeaking, start, stop, pause, resume } = useVAD({
 *   onSpeechStart: () => console.log('开始说话'),
 *   onSpeechEnd: (audio) => sendToServer(audio),
 * })
 * ```
 */
export function useVAD(options: VADOptions = {}) {
  const {
    onSpeechStart,
    onSpeechEnd,
    onVADMisfire,
    onError,
    positiveSpeechThreshold = 0.5,
    negativeSpeechThreshold = 0.35,
    preSpeechPadMs = 100,
    redemptionMs = 300,
    minSpeechMs = 150,
  } = options

  const [state, setState] = useState<VADState>({
    isInitialized: false,
    isListening: false,
    isSpeaking: false,
    isPaused: false,
    isSupported: typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia,
    isLoading: false,
    error: null,
  })

  const vadRef = useRef<MicVAD | null>(null)
  const speechStartTimeRef = useRef<number>(0)

  // 初始化 VAD
  const initialize = useCallback(async () => {
    if (!state.isSupported) {
      const errorMsg = '浏览器不支持 VAD（需要 WebAssembly 和麦克风权限）'
      setState(prev => ({ ...prev, error: errorMsg }))
      onError?.(errorMsg)
      return false
    }

    if (vadRef.current) {
      return true // 已经初始化
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      // VAD 静态资源路径配置
      // VITE_VAD_ASSET_URL: 可选环境变量，指定 VAD 静态资源的完整 URL
      // 例如：https://static.example.com/vad/ 或留空使用相对路径 /
      // 注意：API Gateway 不适合提供大型静态文件（ONNX、WASM），建议使用 CDN 或独立静态服务器
      const vadAssetPath = import.meta.env.VITE_VAD_ASSET_URL || '/'
      console.log('[VAD] 使用资源路径:', vadAssetPath)
      
      const vad = await MicVAD.new({
        // 资源路径配置
        baseAssetPath: vadAssetPath,
        onnxWASMBasePath: vadAssetPath,
        // VAD 参数
        positiveSpeechThreshold,
        negativeSpeechThreshold,
        preSpeechPadMs,
        redemptionMs,
        minSpeechMs,
        onSpeechStart: () => {
          speechStartTimeRef.current = Date.now()
          setState(prev => ({ ...prev, isSpeaking: true }))
          onSpeechStart?.()
        },
        onSpeechEnd: (audio: Float32Array) => {
          const duration = Date.now() - speechStartTimeRef.current
          setState(prev => ({ ...prev, isSpeaking: false }))
          onSpeechEnd?.(audio, duration)
        },
        onVADMisfire: () => {
          setState(prev => ({ ...prev, isSpeaking: false }))
          onVADMisfire?.()
        },
      })

      vadRef.current = vad
      setState(prev => ({ ...prev, isInitialized: true, isLoading: false }))
      return true
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '初始化 VAD 失败'
      setState(prev => ({ ...prev, error: errorMsg, isLoading: false }))
      onError?.(errorMsg)
      return false
    }
  }, [
    state.isSupported,
    positiveSpeechThreshold,
    negativeSpeechThreshold,
    preSpeechPadMs,
    redemptionMs,
    minSpeechMs,
    onSpeechStart,
    onSpeechEnd,
    onVADMisfire,
    onError,
  ])

  // 开始监听
  const start = useCallback(async () => {
    // 先初始化
    if (!vadRef.current) {
      const success = await initialize()
      if (!success) return false
    }

    try {
      vadRef.current?.start()
      setState(prev => ({ ...prev, isListening: true, isPaused: false, error: null }))
      return true
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '启动 VAD 失败'
      setState(prev => ({ ...prev, error: errorMsg }))
      onError?.(errorMsg)
      return false
    }
  }, [initialize, onError])

  // 停止监听
  const stop = useCallback(() => {
    if (vadRef.current) {
      vadRef.current.pause()
      setState(prev => ({ ...prev, isListening: false, isSpeaking: false, isPaused: false }))
    }
  }, [])

  // 暂停（用于 AEC - 播放 TTS 时暂停 VAD）
  const pause = useCallback(() => {
    if (vadRef.current && state.isListening) {
      vadRef.current.pause()
      setState(prev => ({ ...prev, isPaused: true, isSpeaking: false }))
    }
  }, [state.isListening])

  // 恢复
  const resume = useCallback(() => {
    if (vadRef.current && state.isPaused) {
      vadRef.current.start()
      setState(prev => ({ ...prev, isPaused: false }))
    }
  }, [state.isPaused])

  // 销毁
  const destroy = useCallback(() => {
    if (vadRef.current) {
      vadRef.current.destroy()
      vadRef.current = null
      setState(prev => ({
        ...prev,
        isInitialized: false,
        isListening: false,
        isSpeaking: false,
        isPaused: false,
      }))
    }
  }, [])

  // 清理
  useEffect(() => {
    return () => {
      if (vadRef.current) {
        vadRef.current.destroy()
        vadRef.current = null
      }
    }
  }, [])

  return {
    ...state,
    initialize,
    start,
    stop,
    pause,
    resume,
    destroy,
  }
}

/**
 * 将 Float32Array 音频数据转换为 WAV Blob
 * @param audioData Float32Array 音频数据（-1 到 1 范围）
 * @param sampleRate 采样率
 * @returns WAV Blob
 */
export function float32ToWavBlob(audioData: Float32Array, sampleRate: number = 16000): Blob {
  const numChannels = 1
  const bitsPerSample = 16
  const bytesPerSample = bitsPerSample / 8
  const blockAlign = numChannels * bytesPerSample
  const byteRate = sampleRate * blockAlign
  const dataSize = audioData.length * bytesPerSample
  const buffer = new ArrayBuffer(44 + dataSize)
  const view = new DataView(buffer)

  // WAV header
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i))
    }
  }

  writeString(0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true) // fmt chunk size
  view.setUint16(20, 1, true) // audio format (PCM)
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitsPerSample, true)
  writeString(36, 'data')
  view.setUint32(40, dataSize, true)

  // Convert Float32 to Int16 and write audio data
  let offset = 44
  for (let i = 0; i < audioData.length; i++) {
    const sample = Math.max(-1, Math.min(1, audioData[i]))
    const int16Sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF
    view.setInt16(offset, int16Sample, true)
    offset += 2
  }

  return new Blob([buffer], { type: 'audio/wav' })
}

/**
 * 将 Float32Array 音频数据转换为 Base64 WAV
 * @param audioData Float32Array 音频数据
 * @param sampleRate 采样率
 * @returns Promise<string> Base64 编码的 WAV
 */
export async function float32ToBase64Wav(audioData: Float32Array, sampleRate: number = 16000): Promise<string> {
  const wavBlob = float32ToWavBlob(audioData, sampleRate)
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(wavBlob)
  })
}

/**
 * 预加载 VAD 模型（可在应用启动时调用，减少首次使用延迟）
 * 注意：当前版本的 @ricky0123/vad-web 不支持 prefetch，模型会在首次使用时自动加载
 */
export async function preloadVADModel(): Promise<void> {
  // @ricky0123/vad-web 不支持 prefetch API
  // 模型会在 MicVAD.new() 时自动加载
  console.log('[VAD] 模型将在首次使用时加载')
}

